import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { EmailSendBody, EmailSendResponse, Project } from '@/api/client';
import { projectEntityQueryKey, setProjectEntity } from '@/lib/projectEntityCache';
import { patchProjectListCaches } from '@/lib/projectListQueryCache';
import { useToast } from '@/contexts/ToastContext';

export interface UseEmailSendReturn {
  mutate: (payload: EmailSendBody) => void;
  isPending: boolean;
  variables: EmailSendBody | undefined;
}

/**
 * Хук отправки письма. Инкапсулирует:
 * - useMutation для api.emails.send
 * - Обновление кэшей списков проектов (вкл. table-active) при успехе; проект вне списков — в entity-кэше
 * - Показ ошибки через toast
 * - onClose при успехе
 */
export function useEmailSend({ onClose }: { onClose: () => void }): UseEmailSendReturn {
  const queryClient = useQueryClient();
  const { showError } = useToast();

  const mutation = useMutation<EmailSendResponse, Error, EmailSendBody>({
    mutationFn: api.emails.send,
    onSuccess: (data) => {
      const p = data.project;
      if (p) {
        let inLists = false;
        patchProjectListCaches(queryClient, (prev) =>
          prev
            ? prev.map((row) => {
                if (row.id !== p.id) return row;
                inLists = true;
                return { ...p, sessions: p.sessions ?? row.sessions ?? [] };
              })
            : prev
        );
        // Проект, открытый из поиска или по ссылке, есть только в сущности: списки её не обновят, а эха по сокету может не быть
        if (!inLists) {
          const entity = queryClient.getQueryData<Project>(projectEntityQueryKey(p.id));
          setProjectEntity(queryClient, { ...p, sessions: p.sessions ?? entity?.sessions ?? [] });
        }
      }
      onClose();
    },
    onError: (e) => {
      showError(e.message);
    },
  });

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    variables: mutation.variables,
  };
}
